import { OnyxEvent } from '../models/event';
import { Party } from '../models/party';

export class EventUtils {
  static isEventValid(onyxEvent: OnyxEvent, isAgencyUser: boolean): boolean {
    return Boolean(
      onyxEvent &&
      this.isEventBasicInformationValid(onyxEvent, isAgencyUser) &&
      this.isEventStartEndDateValid(onyxEvent) &&
      this.isEventPropertyValid(onyxEvent.eventProperty) &&
      this.isEventAgencyValid(onyxEvent.eventAgency) &&
      this.isEventReferenceNumberValid(onyxEvent.eventProperty, onyxEvent.eventAgency));
  }

  static isEventBasicInformationValid(onyxEvent: OnyxEvent, isAgencyUser: boolean): boolean {
    return Boolean(
      this.getEventName(onyxEvent, isAgencyUser));
  }

  static isEventPropertyValid(eventProperty: Party): boolean {
    return Boolean(eventProperty && eventProperty.name && eventProperty.chain && eventProperty.brand && eventProperty.propertyId);
  }

  static isEventAgencyValid(eventAgency: Party): boolean {
    return Boolean(eventAgency && eventAgency.name);
  }

  static isEventReferenceNumberValid(eventProperty: Party, eventAgency: Party): boolean {
    return Boolean(eventProperty && eventProperty.eventReferenceNumber
                    || eventAgency && eventAgency.eventReferenceNumber);
  }

  static isEventStartEndDateValid(onyxEvent: OnyxEvent): boolean {
    let _propertyDates = true;
    let _agencyDates = true;

    if (onyxEvent.eventProperty) {
      _propertyDates = onyxEvent.eventProperty.startDate &&
        onyxEvent.eventProperty.endDate &&
        new Date(onyxEvent.eventProperty.startDate) < new Date(onyxEvent.eventProperty.endDate);
    }
    if (onyxEvent.eventAgency) {
      _agencyDates = onyxEvent.eventAgency.startDate &&
        onyxEvent.eventAgency.endDate &&
        new Date(onyxEvent.eventAgency.startDate) < new Date(onyxEvent.eventAgency.endDate);
    }
    return Boolean(_propertyDates && _agencyDates);
  }

  static doesEventHaveAgency(onyxEvent: OnyxEvent): boolean {
    return Boolean(onyxEvent && onyxEvent.eventAgency && onyxEvent.eventAgency.code);
  }

  static getEventGroupCode(event: OnyxEvent, isAgencyUser: boolean) {
    let _eventReferenceNumber = '';
    let _propertyReferenceNumber = null;
    let _agencyReferenceNumber = null;
    if (event.eventProperty && event.eventProperty.eventReferenceNumber) {
      _propertyReferenceNumber = event.eventProperty.eventReferenceNumber;
    }
    if (event.eventAgency && event.eventAgency.eventReferenceNumber) {
      _agencyReferenceNumber = event.eventAgency.eventReferenceNumber;
    }
    if (isAgencyUser) {
      if (_agencyReferenceNumber) {
        _eventReferenceNumber = _agencyReferenceNumber;
      } else if (_propertyReferenceNumber) {
        _eventReferenceNumber = _propertyReferenceNumber;
      }
    } else {
      if (_propertyReferenceNumber) {
        _eventReferenceNumber = _propertyReferenceNumber;
      } else if (_agencyReferenceNumber) {
        _eventReferenceNumber = _agencyReferenceNumber;
      }
    }

    return _eventReferenceNumber;
  }

  static getEventStartDate(event: OnyxEvent, isAgencyUser: boolean) {
    let _eventStartDate = '';
    let _propertyStartDate = null;
    let _agencyStartDate = null;
    if (event.eventProperty && event.eventProperty.startDate) {
      _propertyStartDate = event.eventProperty.startDate;
    }
    if (event.eventAgency && event.eventAgency.startDate) {
      _agencyStartDate = event.eventAgency.startDate;
    }
    if (isAgencyUser) {
      if (_agencyStartDate) {
        _eventStartDate = _agencyStartDate;
      } else if (_propertyStartDate) {
        _eventStartDate = _propertyStartDate;
      }
    } else {
      if (_propertyStartDate) {
        _eventStartDate = _propertyStartDate;
      } else if (_agencyStartDate) {
        _eventStartDate = _agencyStartDate;
      }
    }

    return _eventStartDate;
  }

  static getEventEndDate(event: OnyxEvent, isAgencyUser: boolean) {
    let _eventEndDate = '';
    let _propertyEndDate = null;
    let _agencyEndDate = null;
    if (event.eventProperty && event.eventProperty.endDate) {
      _propertyEndDate = event.eventProperty.endDate;
    }
    if (event.eventAgency && event.eventAgency.endDate) {
      _agencyEndDate = event.eventAgency.endDate;
    }
    if (isAgencyUser) {
      if (_agencyEndDate) {
        _eventEndDate = _agencyEndDate;
      } else if (_propertyEndDate) {
        _eventEndDate = _propertyEndDate;
      }
    } else {
      if (_propertyEndDate) {
        _eventEndDate = _propertyEndDate;
      } else if (_agencyEndDate) {
        _eventEndDate = _agencyEndDate;
      }
    }

    return _eventEndDate;
  }

  static getEventName(event: OnyxEvent, isAgencyUser: boolean) {
    let _eventName = '';
    let _propertyName = null;
    let _agencyName = null;
    if (event.eventProperty && event.eventProperty.partyEventName) {
      _propertyName = event.eventProperty.partyEventName;
    }
    if (event.eventAgency && event.eventAgency.partyEventName) {
      _agencyName = event.eventAgency.partyEventName;
    }
    if (isAgencyUser) {
      if (_agencyName) {
        _eventName = _agencyName;
      } else if (_propertyName) {
        _eventName = _propertyName;
      }
    } else {
      if (_propertyName) {
        _eventName = _propertyName;
      } else if (_agencyName) {
        _eventName = _agencyName;
      }
    }

    return _eventName;
  }

  static getEventUserRef(event: OnyxEvent, isAgencyUser: boolean, userRefName: string) {
    let _eventEventUserRef = null;
    let _propertyUserRef = null;
    let _agencyUserRef = null;

    if (event.eventProperty && event.eventProperty[userRefName]) {
      _propertyUserRef = event.eventProperty[userRefName];
    }
    if (event.eventAgency && event.eventAgency[userRefName]) {
      _agencyUserRef = event.eventAgency[userRefName];
    }
    if (isAgencyUser) {
      if (_agencyUserRef) {
        _eventEventUserRef = _agencyUserRef;
      } else if (_propertyUserRef) {
        _eventEventUserRef = _propertyUserRef;
      }
    } else {
      if (_propertyUserRef) {
        _eventEventUserRef = _propertyUserRef;
      } else if (_agencyUserRef) {
        _eventEventUserRef = _agencyUserRef;
      }
    }

    return _eventEventUserRef;
  }

  /**
   * Serialize an OnyxEvent ignoring the isExpanded property from Party objects
   * @param anEvent
   * @returns
   */
  static serializeEvent(anEvent: OnyxEvent): string {
    const eventCopy = anEvent.toJS();
    eventCopy.eventVendors = anEvent.eventVendors.map((eventVendor) => {
      if (eventVendor.toJS) {
        return eventVendor.toJS();
      }
      return { ...eventVendor };
    });
    if (eventCopy.eventAgency) {
      delete eventCopy.eventAgency.isExpanded;
    }
    if (eventCopy.eventProperty) {
      delete eventCopy.eventProperty.isExpanded;
    }
    if (eventCopy.eventClient) {
      delete eventCopy.eventClient.isExpanded;
    }
    if (eventCopy.eventVendors) {
      eventCopy.eventVendors = eventCopy.eventVendors.map(vendor => {
        return { ...vendor, isExpanded: undefined };
      });
    }
    return JSON.stringify(eventCopy);
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/utils/event-utils.ts